import { useEffect, useMemo } from 'react';

import { KeyboardEventType } from '@this+that/utils';

import { formatShortcutWithShift } from '../utils/keyboardGlyphs';
import { isMacPlatform } from '../utils/platform/detectOS';

interface EmailCompositionShortcutsConfig {
  onAddCC?: () => void;
  onAddBCC?: () => void;
  enabled?: boolean;
}

export function useEmailCompositionShortcuts({
  onAddCC,
  onAddBCC,
  enabled = true,
}: Readonly<EmailCompositionShortcutsConfig>) {
  const isMac = useMemo(() => isMacPlatform(), []);

  const shortcuts = useMemo(
    () => ({
      addCC: formatShortcutWithShift('C'),
      addBCC: formatShortcutWithShift('B'),
    }),
    [],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const hasCorrectModifier = isMac
        ? event.metaKey && !event.ctrlKey
        : event.ctrlKey && !event.metaKey;

      if (!hasCorrectModifier || !event.shiftKey) {
        return;
      }

      switch (event.code) {
        case 'KeyC':
          event.preventDefault();
          if (onAddCC) {
            onAddCC();
          }
          break;
        case 'KeyB':
          event.preventDefault();
          if (onAddBCC) {
            onAddBCC();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener(KeyboardEventType.KEYDOWN, handleKeyDown);

    return () => {
      window.removeEventListener(KeyboardEventType.KEYDOWN, handleKeyDown);
    };
  }, [onAddCC, onAddBCC, enabled, isMac]);

  return {
    shortcuts,
  };
}
